// UpdateFrequencyDialog · Lot M5
// Change la cadence de revue. Presets 7/14/30 + saisie libre (1..90).

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter }
  from "@/components/ui/dialog";

const PRESETS = [7, 14, 30];

export default function UpdateFrequencyDialog({
  open, onOpenChange, currentDays, onSubmit,
}) {
  const [selected, setSelected] = useState(currentDays || 7);
  const [customValue, setCustomValue] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (PRESETS.includes(currentDays)) {
      setSelected(currentDays);
      setCustomMode(false);
      setCustomValue("");
    } else if (currentDays) {
      setCustomMode(true);
      setCustomValue(String(currentDays));
      setSelected(currentDays);
    } else {
      setSelected(7);
      setCustomMode(false);
      setCustomValue("");
    }
  }, [open, currentDays]);

  const daysToApply = customMode ? Number(customValue) : selected;
  const valid = Number.isInteger(daysToApply) && daysToApply >= 1 && daysToApply <= 90;
  const changed = valid && daysToApply !== currentDays;

  const submit = async () => {
    if (!valid) return toast.error("Cadence invalide (entre 1 et 90 jours)");
    if (!changed) return onOpenChange(false);
    setSubmitting(true);
    try {
      await onSubmit?.(daysToApply);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" data-testid="frequency-dialog">
        <DialogHeader>
          <DialogTitle className="text-[19px] font-semibold tracking-[-0.01em]">
            Cadence de revue
          </DialogTitle>
          <p className="text-[12.5px] text-black/55 max-w-[400px]">
            La nouvelle cadence s&apos;applique à partir du prochain snapshot.
            Les périodes passées ne sont jamais recalculées.
          </p>
        </DialogHeader>

        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {PRESETS.map(days => {
              const active = !customMode && selected === days;
              return (
                <button
                  key={days}
                  type="button"
                  onClick={() => { setCustomMode(false); setSelected(days); }}
                  className={`rounded-xl px-4 py-3 text-left transition ${
                    active
                      ? "bg-[#C8FF2F] text-black"
                      : "bg-white text-black/70 hover:text-black"
                  }`}
                  style={
                    active
                      ? undefined
                      : { boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }
                  }
                  data-testid={`freq-preset-${days}`}
                >
                  <div className="font-mono text-[20px] leading-none">{days} j</div>
                  <div className="mt-1 text-[10.5px] uppercase tracking-[0.14em] opacity-70">
                    {days === 7 ? "Hebdo" : days === 14 ? "Bimensuel" : "Mensuel"}
                  </div>
                </button>
              );
            })}
          </div>

          <div>
            <button
              type="button"
              onClick={() => setCustomMode(m => !m)}
              className="text-[12px] text-black/50 hover:text-black underline underline-offset-2"
              data-testid="freq-custom-toggle"
            >
              {customMode ? "Utiliser un preset" : "Cadence personnalisée…"}
            </button>
          </div>

          {customMode && (
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] uppercase tracking-[0.14em] text-black/55">
                Jours (1 à 90)
              </span>
              <input
                type="number" min={1} max={90} step={1}
                value={customValue}
                onChange={e => setCustomValue(e.target.value)}
                placeholder="ex. 10"
                autoFocus
                className="h-11 rounded-lg bg-white px-3 text-[15px] outline-none focus:ring-2 focus:ring-[#C8FF2F]/40 font-mono"
                style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
                data-testid="freq-custom-input"
              />
            </label>
          )}
        </div>

        <DialogFooter className="mt-4 gap-2 sm:justify-end">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full bg-white px-4 py-2 text-[13px] font-medium text-black/70 hover:text-black"
            style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
            data-testid="freq-cancel-btn"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !valid || !changed}
            className="rounded-full bg-[#C8FF2F] px-5 py-2 text-[13px] font-medium text-black transition-transform hover:scale-[1.005] disabled:opacity-40 disabled:hover:scale-100"
            data-testid="freq-submit-btn"
          >
            {submitting ? "Application…" : "Appliquer"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
